/**
 * PlasmaEdge — an ambient plasma light along the left edge of a card.
 *
 * Each attached element gets a tiny 12×64 canvas, CSS-scaled to the card's
 * left edge, drawing a classic four-wave sine-field plasma in the card's
 * colour palette. Brightest at the edge, dissolving into the surface.
 * One shared ~30fps loop; off-screen cards are skipped; the tab being hidden
 * pauses everything; prefers-reduced-motion gets a single still frame.
 *
 * Usage (declarative — recommended):
 *   <div class="card" data-plasma-edge style="--edge-a:#059669; --edge-b:#6ee7b7">…</div>
 *   Any element carrying data-plasma-edge is picked up on load and whenever
 *   it is added to the DOM later (Vue re-renders included).
 *
 * Usage (imperative):
 *   PlasmaEdge.attach(el, { a: '#0284c7', b: '#7dd3fc' });
 *   PlasmaEdge.attachAll('.my-card');
 *   PlasmaEdge.refresh(el);   // re-read the palette after a class change
 *   PlasmaEdge.detach(el);
 *
 * Palette resolution order: opts → data-plasma-a / data-plasma-b →
 * CSS custom properties --edge-a / --edge-b → sky-blue default.
 * Width: CSS --plasma-width on the host (default 32px).
 * Speed: data-plasma-speed (default 1). Hover doubles it briefly.
 *
 * The host element becomes position:relative + isolation:isolate so the
 * canvas (z-index:-1 inside that context) sits above the host's own
 * background but beneath all of its content, with no z-index needed on
 * the content itself.
 */
(function () {
    'use strict';
    if (window.PlasmaEdge) return;

    var W = 12, H = 64, FPS = 30;
    var items = new Map();
    var rafId = null, lastFrame = 0;
    var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var DEFAULT_A = [2, 132, 199], DEFAULT_B = [125, 211, 252];

    // ---- styles -------------------------------------------------------------
    var style = document.createElement('style');
    style.setAttribute('data-plasma-edge', '');
    style.textContent = [
        '.plasma-edge-host{position:relative;isolation:isolate}',
        // The wrapper does the clipping: overflow:hidden + inherited radius is honoured
        // everywhere, whereas border-radius on a filtered <canvas> is not (WebKit).
        '.plasma-edge-wrap{position:absolute;top:0;left:0;right:0;bottom:0;overflow:hidden;border-radius:inherit;',
        'z-index:-1;pointer-events:none;-webkit-transform:translateZ(0);transform:translateZ(0)}',
        'canvas.plasma-edge{position:absolute;left:0;top:0;width:var(--plasma-width,32px);height:100%;display:block;',
        'opacity:var(--plasma-opacity,.95);filter:blur(.6px) saturate(1.15)}'
    ].join('');
    (document.head || document.documentElement).appendChild(style);

    // ---- helpers ------------------------------------------------------------
    function parseColor(str) {
        if (!str) return null;
        str = String(str).trim();
        var m = str.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (m) {
            var h = m[1];
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
        }
        m = str.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (m) return [+m[1], +m[2], +m[3]];
        return null;
    }

    function palette(el, opts) {
        opts = opts || {};
        var cs = getComputedStyle(el);
        var a = parseColor(opts.a) || parseColor(el.getAttribute('data-plasma-a')) || parseColor(cs.getPropertyValue('--edge-a')) || DEFAULT_A;
        var b = parseColor(opts.b) || parseColor(el.getAttribute('data-plasma-b')) || parseColor(cs.getPropertyValue('--edge-b')) || DEFAULT_B;
        var speed = parseFloat(opts.speed || el.getAttribute('data-plasma-speed') || '1') || 1;
        return { a: a, b: b, speed: speed };
    }

    var io = ('IntersectionObserver' in window)
        ? new IntersectionObserver(function (entries) {
            entries.forEach(function (e) { var it = items.get(e.target); if (it) it.visible = e.isIntersecting; });
        }, { rootMargin: '80px' })
        : null;

    // ---- drawing ------------------------------------------------------------
    function draw(it) {
        var d = it.img.data, a = it.a, b = it.b, t = it.t, i = 0;
        var ar = a[0], ag = a[1], ab = a[2], br = b[0], bg = b[1], bb = b[2];
        for (var y = 0; y < H; y++) {
            for (var x = 0; x < W; x++) {
                var v = Math.sin(x * 0.42 + t)
                      + Math.sin(y * 0.16 - t * 0.71)
                      + Math.sin((x * 0.25 + y * 0.12) + t * 0.53)
                      + Math.sin(Math.hypot(x - 4, y - 32) * 0.18 - t * 0.87);
                var n = (v + 4) / 8;                 // 0..1
                var s = n * n * (3 - 2 * n);         // smoothstep — softer bands
                var hi = Math.max(0, s - 0.78) * 4.5; // white bloom at the peaks
                var edge = 1 - x / (W - 1);          // 1 at the edge → 0 inside
                d[i]     = Math.min(255, ar + (br - ar) * s + 255 * hi);
                d[i + 1] = Math.min(255, ag + (bg - ag) * s + 255 * hi);
                d[i + 2] = Math.min(255, ab + (bb - ab) * s + 255 * hi);
                d[i + 3] = edge * edge * (0.35 + 0.65 * s) * 255;
                i += 4;
            }
        }
        it.ctx.putImageData(it.img, 0, 0);
    }

    function loop(now) {
        rafId = requestAnimationFrame(loop);
        if (document.hidden) return;
        var dt = now - lastFrame;
        if (dt < 1000 / FPS) return;
        lastFrame = now;
        var step = Math.min(dt, 100) / 1000;
        items.forEach(function (it, el) {
            if (!el.isConnected) { detach(el); return; }
            if (!it.visible) return;
            var target = it.hover ? 2.2 * it.speed : it.speed;
            it.vel += (target - it.vel) * 0.08;
            it.t += step * 0.45 * it.vel;
            draw(it);
        });
        if (!items.size) { cancelAnimationFrame(rafId); rafId = null; }
    }

    function ensureLoop() {
        if (!reduced && rafId === null && items.size) rafId = requestAnimationFrame(loop);
    }

    // ---- public API ---------------------------------------------------------
    function attach(el, opts) {
        if (!el || items.has(el)) return;
        // A framework re-render (Vue patching, cloneNode) can hand us a node
        // that already carries a canvas we no longer track — reuse it rather
        // than stacking a second one.
        var wrap = null, c = null;
        for (var k = 0; k < el.children.length; k++) {
            if (el.children[k].classList && el.children[k].classList.contains('plasma-edge-wrap')) { wrap = el.children[k]; break; }
        }
        if (wrap) c = wrap.querySelector('canvas.plasma-edge');
        if (!wrap || !c) {
            if (wrap) wrap.parentNode.removeChild(wrap);
            wrap = document.createElement('div');
            wrap.className = 'plasma-edge-wrap';
            wrap.setAttribute('aria-hidden', 'true');
            c = document.createElement('canvas');
            c.className = 'plasma-edge';
            c.width = W; c.height = H;
            wrap.appendChild(c);
            el.insertBefore(wrap, el.firstChild);
        }
        el.classList.add('plasma-edge-host');
        var p = palette(el, opts);
        var it = {
            c: c, wrap: wrap, ctx: c.getContext('2d'), img: null,
            a: p.a, b: p.b, speed: p.speed, vel: p.speed,
            t: Math.random() * 1000, visible: true, hover: false,
            onEnter: function () { it.hover = true; },
            onLeave: function () { it.hover = false; }
        };
        it.img = it.ctx.createImageData(W, H);
        items.set(el, it);
        el.addEventListener('mouseenter', it.onEnter);
        el.addEventListener('mouseleave', it.onLeave);
        if (io) io.observe(el);
        draw(it);
        ensureLoop();
    }

    function detach(el) {
        var it = items.get(el);
        if (!it) return;
        el.removeEventListener('mouseenter', it.onEnter);
        el.removeEventListener('mouseleave', it.onLeave);
        if (io) io.unobserve(el);
        if (it.wrap && it.wrap.parentNode) it.wrap.parentNode.removeChild(it.wrap);
        el.classList.remove('plasma-edge-host');
        items.delete(el);
    }

    function refresh(el, opts) {
        var it = items.get(el);
        if (!it) return;
        var p = palette(el, opts);
        it.a = p.a; it.b = p.b; it.speed = p.speed;
        draw(it);
    }

    function attachAll(selector, opts) {
        document.querySelectorAll(selector).forEach(function (el) { attach(el, opts); });
    }

    // ---- declarative: [data-plasma-edge] now and whenever it appears --------
    var SEL = '[data-plasma-edge]';
    function scan(root) {
        if (!root || root.nodeType !== 1) return;
        if (root.matches && root.matches(SEL)) attach(root);
        if (root.querySelectorAll) root.querySelectorAll(SEL).forEach(function (el) { attach(el); });
    }
    function boot() {
        scan(document.body);
        if ('MutationObserver' in window) {
            new MutationObserver(function (muts) {
                for (var i = 0; i < muts.length; i++) {
                    var m = muts[i];
                    if (m.type === 'childList') {
                        for (var j = 0; j < m.addedNodes.length; j++) scan(m.addedNodes[j]);
                    } else if (m.type === 'attributes' && m.target.nodeType === 1) {
                        // A class swap (Vue state change) may mean a new palette
                        if (items.has(m.target)) refresh(m.target);
                        else if (m.target.matches(SEL)) attach(m.target);
                    }
                }
            }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-plasma-edge', 'style'] });
        }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();

    window.PlasmaEdge = { attach: attach, attachAll: attachAll, detach: detach, refresh: refresh };
})();
